import React, { useState, useEffect, useRef } from "react";
import { db, auth } from "../firebase";
import {
  collection, onSnapshot, doc, updateDoc, addDoc,
  serverTimestamp, getDocs, query, where
} from "firebase/firestore";

const NUM_SLOTS        = 8;
const LOAN_MINUTES     = 60;
const PICKUP_TIMEOUT_S = 120;

// Slot → calculator model mapping — reorder as needed
const SLOT_MODELS = {
  2: { name: "Casio FX-85GT Plus",      img: "/fx85gt.png",   short: "FX-85GT" },
  1: { name: "Casio FX-991ES Plus #1",  img: "/fx991es.png",  short: "FX-991ES" },
  3: { name: "Casio FX-991ES Plus #2",  img: "/fx991es.png",  short: "FX-991ES" },
  5: { name: "Casio FX-991ES Plus #3",  img: "/fx991es.png",  short: "FX-991ES" },
  4: { name: "Sharp EL-531WH",          img: "/sharp531.png", short: "EL-531" },
  6: { name: "Casio FX-83GT CW",        img: "/fx83gt.png",   short: "FX-83GT" },
  7: { name: "FX-991 Plus 2nd Ed. #1",  img: "/fx991plus.png",short: "FX-991+" },
  8: { name: "FX-991 Plus 2nd Ed. #2",  img: "/fx991plus.png",short: "FX-991+" },
};

const theme = {
  light: {
    bg: "#f8fafc", bg2: "#f1f5f9", bg3: "#e9eef5", border: "#e2e8f0",
    text1: "#0f172a", text2: "#64748b", text3: "#94a3b8",
    blue: "#2563eb", blueBg: "#eff6ff", blueBorder: "#bfdbfe",
    green: "#16a34a", greenBg: "#f0fdf4", greenBorder: "#bbf7d0",
    red: "#dc2626", redBg: "#fef2f2", redBorder: "#fecaca",
    orange: "#ea580c", orangeBg: "#fff7ed", orangeBorder: "#fed7aa",
    cardBg: "#ffffff", shadow: "0 1px 3px rgba(0,0,0,0.08)",
  },
  dark: {
    bg: "#0f172a", bg2: "#131f30", bg3: "#1a2740", border: "#273549",
    text1: "#f1f5f9", text2: "#94a3b8", text3: "#475569",
    blue: "#378ADD", blueBg: "#1e3a5f", blueBorder: "#1d4ed8",
    green: "#4ade80", greenBg: "#052e16", greenBorder: "#166534",
    red: "#f87171", redBg: "#2d1515", redBorder: "#7f1d1d",
    orange: "#fb923c", orangeBg: "#2d1a00", orangeBorder: "#92400e",
    cardBg: "#1e293b", shadow: "0 1px 3px rgba(0,0,0,0.4)",
  },
};

function getSlotNumber(slot) {
  if (slot.docId && slot.docId.startsWith("slot_")) {
    const n = parseInt(slot.docId.replace("slot_", ""), 10);
    if (!isNaN(n)) return n;
  }
  if (slot.id !== undefined && slot.id !== null) return slot.id;
  return "?";
}

function formatCountdown(seconds) {
  if (seconds <= 0) return "00:00";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

// ── Station Grid ───────────────────────────────────────────
function StationGrid({ slots, highlightSlot, t }) {
  const getSlotData = (num) => {
    const found = slots.find(s => {
      const n = parseInt((s.docId || "").replace("slot_", ""), 10) || s.id;
      return n === num;
    });
    return { present: found?.present ?? true, authorized: found?.authorized ?? false };
  };

  return (
    <div style={{
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: 8,
      marginBottom: 16,
    }}>
      {Array.from({ length: NUM_SLOTS }, (_, i) => {
        const num     = i + 1;
        const model   = SLOT_MODELS[num];
        const slot    = getSlotData(num);
        const isHL    = highlightSlot === num;
        const present = slot.present;

        const borderColor = isHL ? "#2563eb"
          : present ? "#22c55e" : "#ef4444";
        const bgColor = isHL ? "rgba(37,99,235,0.08)"
          : present ? "rgba(34,197,94,0.06)" : "rgba(239,68,68,0.06)";
        const statusColor = isHL ? "#2563eb"
          : present ? "#22c55e" : "#ef4444";
        const statusLabel = isHL ? "Sélectionné"
          : present ? "Disponible" : "Emprunté";

        return (
          <div key={num} style={{
            background: t.cardBg,
            border: `2px solid ${borderColor}`,
            borderRadius: 12,
            padding: "10px 8px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 6,
            position: "relative",
            animation: isHL ? "labelBlink 1s ease infinite" : "none",
            boxShadow: isHL ? `0 0 16px ${borderColor}44` : t.shadow,
          }}>
            {/* Slot number */}
            <div style={{
              position: "absolute", top: 6, left: 8,
              fontSize: 9, fontWeight: 700, color: t.text3,
              fontFamily: "monospace",
            }}>
              #{String(num).padStart(2, "0")}
            </div>

            {/* Status LED */}
            <div style={{
              position: "absolute", top: 8, right: 8,
              width: 8, height: 8, borderRadius: "50%",
              background: statusColor,
              boxShadow: `0 0 6px ${statusColor}`,
            }} />

            {/* Calculator image */}
            <div style={{
              width: "100%", height: 80,
              display: "flex", alignItems: "center", justifyContent: "center",
              marginTop: 4,
            }}>
              <img
                src={model.img}
                alt={model.name}
                style={{
                  maxWidth: "100%", maxHeight: "100%",
                  objectFit: "contain",
                  opacity: present ? 1 : 0.35,
                  filter: present ? "none" : "grayscale(60%)",
                }}
                onError={e => {
                  e.target.style.display = "none";
                  e.target.nextSibling.style.display = "flex";
                }}
              />
              {/* Fallback if no image */}
              <div style={{
                display: "none", fontSize: 32,
                alignItems: "center", justifyContent: "center",
              }}>🧮</div>
            </div>

            {/* Model short name */}
            <div style={{
              fontSize: 10, fontWeight: 700,
              color: t.text1, textAlign: "center",
              fontFamily: "monospace", lineHeight: 1.2,
            }}>
              {model.short}
            </div>

            {/* Status badge */}
            <div style={{
              fontSize: 9, fontWeight: 600,
              color: statusColor,
              background: bgColor,
              border: `1px solid ${borderColor}`,
              borderRadius: 20, padding: "2px 8px",
            }}>
              {statusLabel}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Animated return arrows ─────────────────────────────────
function ReturnArrows() {
  const [step, setStep] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => setStep(s => (s + 1) % 3), 500);
    return () => clearInterval(interval);
  }, []);
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0, margin: "8px auto 0" }}>
      {[0, 1, 2].map(i => {
        const lit = i <= step;
        return (
          <svg key={i} width="40" height="24" viewBox="0 0 40 24" fill="none" style={{ display: "block", marginTop: i === 0 ? 0 : -6 }}>
            <path d="M4 4L20 20L36 4" stroke={lit ? "#2563eb" : "rgba(37,99,235,0.18)"} strokeWidth={lit ? "4" : "2.5"} strokeLinecap="round" strokeLinejoin="round" style={{ transition: "stroke 0.15s, stroke-width 0.15s" }}/>
          </svg>
        );
      })}
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────
export default function CalculatorPage() {
  const [dark, setDark]                       = useState(() => localStorage.getItem("ws-theme") === "dark");
  const [slots, setSlots]                     = useState([]);
  const [step, setStep]                       = useState("idle");
  const [assignedSlot, setAssignedSlot]       = useState(null);
  const [secondsLeft, setSecondsLeft]         = useState(LOAN_MINUTES * 60);
  const [pickupSecondsLeft, setPickupSeconds] = useState(PICKUP_TIMEOUT_S);
  const [activeBorrow, setActiveBorrow]       = useState(null);
  const timerRef       = useRef(null);
  const pickupTimerRef = useRef(null);
  const logDocRef      = useRef(null);

  const t = dark ? theme.dark : theme.light;

  const toggleTheme = () => setDark(d => {
    localStorage.setItem("ws-theme", !d ? "dark" : "light");
    return !d;
  });

  // Firestore slots listener
  useEffect(() => {
    let debounceTimer = null;
    let pending = null;
    const unsub = onSnapshot(collection(db, "calculator_slots"), snap => {
      const data = snap.docs.map(d => ({ docId: d.id, ...d.data() }));
      data.sort((a, b) => getSlotNumber(a) - getSlotNumber(b));
      pending = data;
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => setSlots(pending), 500);
    });
    return () => { unsub(); clearTimeout(debounceTimer); };
  }, []);

  // Check for active borrow
  useEffect(() => {
    const checkActiveBorrow = async () => {
      const user = auth.currentUser;
      if (!user) return;
      const q = query(
        collection(db, "calculator_logs"),
        where("userId", "==", user.uid),
        where("status", "in", ["reserved", "active"])
      );
      const snap = await getDocs(q);
      if (!snap.empty) {
        const logDoc = snap.docs[0];
        setActiveBorrow({ id: logDoc.id, ...logDoc.data() });
      } else {
        setActiveBorrow(null);
      }
    };
    checkActiveBorrow();
  }, [step]);

  // Countdown timer
  useEffect(() => {
    if (step === "countdown") {
      setSecondsLeft(LOAN_MINUTES * 60);
      timerRef.current = setInterval(() => {
        setSecondsLeft(prev => {
          if (prev <= 1) { clearInterval(timerRef.current); setStep("overdue"); return 0; }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timerRef.current);
  }, [step]);

  // Pickup timeout
  useEffect(() => {
    if (step === "assigned") {
      setPickupSeconds(PICKUP_TIMEOUT_S);
      pickupTimerRef.current = setInterval(() => {
        setPickupSeconds(prev => {
          if (prev <= 1) {
            clearInterval(pickupTimerRef.current);
            if (assignedSlot) updateDoc(doc(db, "calculator_slots", assignedSlot.docId), { authorized: false }).catch(console.error);
            if (logDocRef.current) {
              updateDoc(logDocRef.current, { returnedAt: serverTimestamp(), status: "cancelled" }).catch(console.error);
              logDocRef.current = null;
            }
            setStep("cancelled"); setAssignedSlot(null); return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      clearInterval(pickupTimerRef.current);
    }
    return () => clearInterval(pickupTimerRef.current);
  }, [step, assignedSlot]);

  // Watch for return detection
  useEffect(() => {
    if (step !== "waiting" || !assignedSlot) return;
    const slotNum = getSlotNumber(assignedSlot);
    const found = slots.find(s => getSlotNumber(s) === slotNum);
    if (found && found.present) handleSlotDetected();
  }, [step, slots, assignedSlot]);

  const allSlotsLoaded = slots.length === NUM_SLOTS;
  const availableCount = allSlotsLoaded ? slots.filter(s => s.present && !s.authorized).length : null;

  const getRecommended = () => {
    if (!allSlotsLoaded) return null;
    const available = slots.filter(s => s.present && !s.authorized);
    if (available.length === 0) return null;
    return available.reduce((a, b) => (a.secondsIn || 0) > (b.secondsIn || 0) ? a : b);
  };

  const handleResumeBorrow = () => {
    if (!activeBorrow) return;
    const slotDoc = slots.find(s => s.docId === activeBorrow.slotDocId);
    if (slotDoc) setAssignedSlot(slotDoc);
    else setAssignedSlot({ docId: activeBorrow.slotDocId, id: activeBorrow.slotId });
    logDocRef.current = doc(db, "calculator_logs", activeBorrow.id);
    setStep("returning");
  };

  const handleRequest = async () => {
    const slot = getRecommended();
    if (!slot) { setStep("none"); return; }
    const user = auth.currentUser;
    try {
      if (activeBorrow) { setStep("already_borrowed"); return; }
      await updateDoc(doc(db, "calculator_slots", slot.docId), { authorized: true });
      const logRef = await addDoc(collection(db, "calculator_logs"), {
        slotId:    getSlotNumber(slot),
        slotDocId: slot.docId,
        modelName: SLOT_MODELS[getSlotNumber(slot)]?.name || "Calculatrice",
        userId:    user?.uid ?? "unknown",
        userName:  user?.displayName ?? user?.email ?? "Inconnu",
        takenAt:   serverTimestamp(),
        returnedAt: null,
        status:    "reserved",
      });
      logDocRef.current = logRef;
      setAssignedSlot(slot);
      setStep("assigned");
    } catch (err) { console.error(err); }
  };

  const handleConfirmGood = async () => {
    if (logDocRef.current) await updateDoc(logDocRef.current, { status: "active", condition: "good" }).catch(console.error);
    setStep("countdown");
  };

  const handleConfirmDefective = async () => {
    if (logDocRef.current) await updateDoc(logDocRef.current, { status: "active", condition: "defective" }).catch(console.error);
    if (assignedSlot) await updateDoc(doc(db, "calculator_slots", assignedSlot.docId), { defective: true }).catch(console.error);
    setStep("countdown");
  };

  const handleReturn      = () => { clearInterval(timerRef.current); setStep("returning"); };
  const handleReturnConfirm = () => setStep("waiting");

  const handleSlotDetected = async () => {
    if (logDocRef.current) {
      await updateDoc(logDocRef.current, { returnedAt: serverTimestamp(), status: "returned" }).catch(console.error);
      logDocRef.current = null;
    }
    setActiveBorrow(null);
    setStep("done");
    setTimeout(() => { setStep("idle"); setAssignedSlot(null); }, 3000);
  };

  const radius           = 54;
  const circumference    = 2 * Math.PI * radius;
  const strokeDashoffset = circumference * (1 - secondsLeft / (LOAN_MINUTES * 60));
  const isUrgent         = secondsLeft < 300;
  const slotNumber       = assignedSlot ? getSlotNumber(assignedSlot) : null;
  const slotDisplay      = slotNumber !== null ? String(slotNumber).padStart(2, "0") : "??";
  const slotModel        = slotNumber ? SLOT_MODELS[slotNumber] : null;
  const highlightSlot    = step === "assigned" && slotNumber !== "?" ? slotNumber : null;

  return (
    <div style={{ background: t.bg, minHeight: "100vh", fontFamily: "'DM Sans', 'Segoe UI', sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
        @keyframes fadeIn    { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
        @keyframes pop       { 0%{transform:scale(0.85);opacity:0} 70%{transform:scale(1.05)} 100%{transform:scale(1);opacity:1} }
        @keyframes pulse     { 0%,100%{opacity:1} 50%{opacity:0.4} }
        @keyframes shake     { 0%,100%{transform:translateX(0)} 25%{transform:translateX(-6px)} 75%{transform:translateX(6px)} }
        @keyframes spin      { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        @keyframes labelBlink{ 0%,100%{opacity:1} 50%{opacity:0.3} }
      `}</style>

      <div style={{ maxWidth: 400, margin: "0 auto", padding: "0 18px", minHeight: "100vh", display: "flex", flexDirection: "column" }}>

        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: 16 }}>
          <img src="/logo.PNG" alt="WeStudy" style={{ height: 28, objectFit: "contain" }} onError={e => e.target.style.display = "none"} />
          <button onClick={toggleTheme} style={{ width: 36, height: 20, borderRadius: 20, cursor: "pointer", position: "relative", background: t.bg3, border: `1px solid ${t.border}` }}>
            <div style={{ width: 14, height: 14, borderRadius: "50%", background: t.blue, position: "absolute", top: 2, left: 2, transition: "transform 0.2s", transform: dark ? "translateX(16px)" : "translateX(0)" }} />
          </button>
        </div>

        <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", paddingBottom: 60 }}>

          {/* ── IDLE ── */}
          {step === "idle" && (
            <div style={{ animation: "fadeIn 0.3s ease", textAlign: "center" }}>
              <StationGrid slots={slots} highlightSlot={null} t={t} />
              <div style={{ fontSize: 22, fontWeight: 800, color: t.text1, marginBottom: 8 }}>Station Calculatrices</div>

              {activeBorrow && (
                <div style={{ background: t.orangeBg, border: `1px solid ${t.orangeBorder}`, borderRadius: 12, padding: "12px 16px", marginBottom: 20, textAlign: "left" }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: t.orange, marginBottom: 4 }}>🧮 Emprunt en cours — Slot {String(activeBorrow.slotId).padStart(2, "0")}</div>
                  <div style={{ fontSize: 12, color: t.text2 }}>Vous avez une calculatrice non remise. Pensez à la remettre !</div>
                </div>
              )}

              <div style={{ fontSize: 14, color: t.text2, marginBottom: 20, lineHeight: 1.6 }}>
                {!allSlotsLoaded
                  ? "Chargement des slots…"
                  : availableCount > 0
                    ? `${availableCount} calculatrice${availableCount > 1 ? "s" : ""} disponible${availableCount > 1 ? "s" : ""}`
                    : "Aucune calculatrice disponible"
                }
              </div>

              {activeBorrow ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  <button onClick={handleResumeBorrow} style={{ width: "100%", border: "none", borderRadius: 14, background: t.orange, color: "#fff", fontSize: 16, fontWeight: 700, padding: 16, cursor: "pointer" }}>
                    🧮 Remettre ma calculatrice — Slot {String(activeBorrow.slotId).padStart(2, "0")}
                  </button>
                  <button disabled style={{ width: "100%", border: "none", borderRadius: 14, background: t.bg3, color: t.text3, fontSize: 14, fontWeight: 600, padding: 12, cursor: "not-allowed", opacity: 0.5 }}>
                    🧮 Emprunter une calculatrice
                  </button>
                </div>
              ) : (
                <button onClick={handleRequest} disabled={!allSlotsLoaded || availableCount === 0}
                  style={{ width: "100%", border: "none", borderRadius: 14, background: allSlotsLoaded && availableCount > 0 ? t.blue : t.bg3, color: allSlotsLoaded && availableCount > 0 ? "#fff" : t.text3, fontSize: 16, fontWeight: 700, padding: 16, cursor: allSlotsLoaded && availableCount > 0 ? "pointer" : "not-allowed", opacity: allSlotsLoaded && availableCount > 0 ? 1 : 0.6 }}>
                  🧮 Emprunter une calculatrice
                </button>
              )}
              <div style={{ fontSize: 11, color: t.text3, marginTop: 14 }}>Durée de prêt : {LOAN_MINUTES} minutes • Retour le jour même</div>
            </div>
          )}

          {/* ── ASSIGNED ── */}
          {step === "assigned" && (
            <div style={{ animation: "fadeIn 0.3s ease", textAlign: "center" }}>
              <StationGrid slots={slots} highlightSlot={highlightSlot} t={t} />
              <div style={{ fontSize: 13, fontWeight: 600, color: t.text3, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 10 }}>Rendez-vous au slot</div>
              <div style={{ width: 120, height: 120, borderRadius: "50%", background: t.blueBg, border: `3px solid ${t.blue}`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px", animation: "pop 0.4s ease", boxShadow: `0 0 32px ${t.blue}44` }}>
                <span style={{ fontSize: 54, fontWeight: 900, color: t.blue, lineHeight: 1 }}>{slotDisplay}</span>
              </div>
              {slotModel && (
                <div style={{ fontSize: 13, fontWeight: 600, color: t.text1, marginBottom: 8 }}>{slotModel.name}</div>
              )}
              <div style={{ fontSize: 14, color: t.text2, marginBottom: 8, lineHeight: 1.6 }}>Récupérez votre calculatrice dans le slot <strong style={{ color: t.text1 }}>{slotDisplay}</strong></div>
              <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: t.orangeBg, border: `1px solid ${t.orangeBorder}`, borderRadius: 20, padding: "4px 12px", marginBottom: 8 }}>
                <span style={{ fontSize: 12, color: t.orange, fontWeight: 600 }}>⏱ Vous avez {LOAN_MINUTES} min pour la remettre</span>
              </div>
              <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: pickupSecondsLeft < 30 ? t.redBg : t.bg2, border: `1px solid ${pickupSecondsLeft < 30 ? t.redBorder : t.border}`, borderRadius: 20, padding: "4px 12px", marginBottom: 20, animation: pickupSecondsLeft < 30 ? "pulse 1s infinite" : "none" }}>
                <span style={{ fontSize: 12, color: pickupSecondsLeft < 30 ? t.red : t.text3, fontWeight: 600 }}>Réservation annulée dans {formatCountdown(pickupSecondsLeft)}</span>
              </div>
              <div style={{ fontSize: 13, fontWeight: 600, color: t.text2, marginBottom: 12 }}>Une fois récupérée, elle fonctionne bien ?</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <button onClick={handleConfirmGood} style={{ width: "100%", border: "none", borderRadius: 14, background: t.green, color: "#fff", fontSize: 14, fontWeight: 700, padding: 14, cursor: "pointer" }}>✅ J'ai récupéré ma calculatrice — elle fonctionne bien</button>
                <button onClick={handleConfirmDefective} style={{ width: "100%", border: `1.5px solid ${t.redBorder}`, borderRadius: 14, background: t.redBg, color: t.red, fontSize: 14, fontWeight: 700, padding: 14, cursor: "pointer" }}>⚠️ J'ai récupéré ma calculatrice — elle est défectueuse</button>
              </div>
            </div>
          )}

          {/* ── COUNTDOWN ── */}
          {step === "countdown" && (
            <div style={{ animation: "fadeIn 0.3s ease", textAlign: "center" }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: t.text3, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 20 }}>Temps restant</div>
              <div style={{ position: "relative", width: 160, height: 160, margin: "0 auto 24px" }}>
                <svg width="160" height="160" style={{ transform: "rotate(-90deg)" }}>
                  <circle cx="80" cy="80" r={radius} fill="none" stroke={t.bg3} strokeWidth="8"/>
                  <circle cx="80" cy="80" r={radius} fill="none" stroke={isUrgent ? t.red : t.blue} strokeWidth="8" strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={strokeDashoffset} style={{ transition: "stroke-dashoffset 1s linear, stroke 0.5s" }}/>
                </svg>
                <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                  <div style={{ fontSize: 28, fontWeight: 900, color: isUrgent ? t.red : t.text1, fontVariantNumeric: "tabular-nums", animation: isUrgent ? "pulse 1s infinite" : "none" }}>{formatCountdown(secondsLeft)}</div>
                  <div style={{ fontSize: 10, color: t.text3, marginTop: 2 }}>min:sec</div>
                </div>
              </div>
              {slotModel && <div style={{ fontSize: 13, fontWeight: 600, color: t.text1, marginBottom: 4 }}>{slotModel.name}</div>}
              <div style={{ fontSize: 14, color: t.text2, marginBottom: 6 }}>Remettez la calculatrice au slot <strong style={{ color: t.text1 }}>{slotDisplay}</strong></div>
              <div style={{ fontSize: 12, color: t.text3, marginBottom: 28 }}>avant la fin du décompte</div>
              <button onClick={handleReturn} style={{ width: "100%", border: "none", borderRadius: 14, background: t.blue, color: "#fff", fontSize: 15, fontWeight: 700, padding: 15, cursor: "pointer" }}>🧮 J'ai remis la calculatrice</button>
            </div>
          )}

          {/* ── OVERDUE ── */}
          {step === "overdue" && (
            <div style={{ animation: "shake 0.4s ease", textAlign: "center" }}>
              <div style={{ width: 80, height: 80, borderRadius: "50%", background: t.redBg, border: `2px solid ${t.red}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 36, margin: "0 auto 20px" }}>⚠️</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: t.red, marginBottom: 8 }}>Temps écoulé !</div>
              <div style={{ fontSize: 13, color: t.text2, marginBottom: 28, lineHeight: 1.6 }}>Veuillez remettre la calculatrice au slot <strong style={{ color: t.text1 }}>{slotDisplay}</strong> immédiatement.</div>
              <button onClick={handleReturn} style={{ width: "100%", border: "none", borderRadius: 14, background: t.red, color: "#fff", fontSize: 15, fontWeight: 700, padding: 15, cursor: "pointer" }}>🧮 Remettre la calculatrice</button>
            </div>
          )}

          {/* ── RETURNING ── */}
          {step === "returning" && (
            <div style={{ animation: "fadeIn 0.3s ease", textAlign: "center" }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: t.text3, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 20 }}>Remise en place</div>
              {slotModel && (
                <div style={{ marginBottom: 8 }}>
                  <img src={slotModel.img} alt={slotModel.name} style={{ height: 120, objectFit: "contain" }} onError={e => e.target.style.display = "none"} />
                </div>
              )}
              <ReturnArrows />
              <div style={{ fontSize: 18, fontWeight: 800, color: t.text1, marginBottom: 6, marginTop: 20 }}>Slot <span style={{ color: t.blue }}>{slotDisplay}</span> — Remettez la calculatrice</div>
              <div style={{ fontSize: 13, color: t.text2, marginBottom: 32, lineHeight: 1.6 }}>Appuyez jusqu'au déclic.</div>
              <button onClick={handleReturnConfirm} style={{ width: "100%", border: "none", borderRadius: 14, background: t.green, color: "#fff", fontSize: 15, fontWeight: 700, padding: 15, cursor: "pointer" }}>✅ Calculatrice remise en place</button>
            </div>
          )}

          {/* ── WAITING ── */}
          {step === "waiting" && (
            <div style={{ animation: "fadeIn 0.3s ease", textAlign: "center" }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: t.text3, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 32 }}>Détection en cours</div>
              <div style={{ position: "relative", width: 100, height: 100, margin: "0 auto 28px" }}>
                <svg width="100" height="100" style={{ animation: "spin 1.2s linear infinite" }}>
                  <circle cx="50" cy="50" r="42" fill="none" stroke={t.bg3} strokeWidth="6"/>
                  <circle cx="50" cy="50" r="42" fill="none" stroke={t.blue} strokeWidth="6" strokeLinecap="round" strokeDasharray="70 194"/>
                </svg>
                <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 32 }}>🧮</div>
              </div>
              <div style={{ fontSize: 17, fontWeight: 700, color: t.text1, marginBottom: 8 }}>En attente de détection…</div>
              <div style={{ fontSize: 13, color: t.text2, lineHeight: 1.6 }}>Assurez-vous que la calculatrice est bien<br />insérée dans le slot <strong style={{ color: t.blue }}>{slotDisplay}</strong>.</div>
            </div>
          )}

          {/* ── DONE ── */}
          {step === "done" && (
            <div style={{ animation: "pop 0.4s ease", textAlign: "center" }}>
              <div style={{ width: 80, height: 80, borderRadius: "50%", background: t.greenBg, border: `2px solid ${t.green}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 36, margin: "0 auto 20px" }}>✅</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: t.text1, marginBottom: 8 }}>Merci !</div>
              <div style={{ fontSize: 13, color: t.text2 }}>Calculatrice remise avec succès.</div>
            </div>
          )}

          {/* ── CANCELLED ── */}
          {step === "cancelled" && (
            <div style={{ animation: "fadeIn 0.3s ease", textAlign: "center" }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>⏰</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: t.text1, marginBottom: 8 }}>Réservation annulée</div>
              <div style={{ fontSize: 13, color: t.text2, marginBottom: 32, lineHeight: 1.6 }}>Vous n'avez pas récupéré la calculatrice à temps.<br />Le slot a été libéré.</div>
              <button onClick={() => setStep("idle")} style={{ border: "none", borderRadius: 12, background: t.blue, color: "#fff", fontSize: 14, fontWeight: 600, padding: "12px 24px", cursor: "pointer" }}>← Réessayer</button>
            </div>
          )}

          {/* ── ALREADY BORROWED ── */}
          {step === "already_borrowed" && (
            <div style={{ animation: "fadeIn 0.3s ease", textAlign: "center" }}>
              <div style={{ width: 80, height: 80, borderRadius: "50%", background: t.orangeBg, border: `2px solid ${t.orange}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 36, margin: "0 auto 20px" }}>⚠️</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: t.text1, marginBottom: 8 }}>Emprunt en cours</div>
              <div style={{ fontSize: 13, color: t.text2, marginBottom: 32, lineHeight: 1.6 }}>Vous avez déjà une calculatrice en cours d'utilisation.<br />Remettez-la avant d'en emprunter une nouvelle.</div>
              <button onClick={() => setStep("idle")} style={{ border: "none", borderRadius: 12, background: t.bg3, color: t.text2, fontSize: 14, fontWeight: 600, padding: "12px 24px", cursor: "pointer" }}>← Retour</button>
            </div>
          )}

          {/* ── NONE ── */}
          {step === "none" && (
            <div style={{ animation: "fadeIn 0.3s ease", textAlign: "center" }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>😔</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: t.text1, marginBottom: 8 }}>Aucune calculatrice disponible</div>
              <div style={{ fontSize: 13, color: t.text2, marginBottom: 32 }}>Tous les slots sont occupés pour le moment.</div>
              <button onClick={() => setStep("idle")} style={{ border: "none", borderRadius: 12, background: t.bg3, color: t.text2, fontSize: 14, fontWeight: 600, padding: "12px 24px", cursor: "pointer" }}>← Réessayer</button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}